import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getAllProductSpecificPackaging,
  addProductSpecificPackaging,
  updateProductSpecificPackaging,
  deleteProductSpecificPackaging,
} from "../../../../../../apis/Product/ProductSpecificPackagingApi";
import { getProductSpecificPackagingMaterialDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { IconButton, Tooltip } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import InfoIcon from "app/NewProductCategories/ProductDetails/components/InfoIcon";
import TipBox from "components/TipBox";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration - All 16 categories (same as product component)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const normStr = (s) =>
  s == null || typeof s !== "string" ? "" : s.trim().toLowerCase();

/** Edit distance for typo-tolerant match (e.g. "fot" vs "for" in long labels). */
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

/**
 * Match packaging dropdown row by code, value, name, or title (case-insensitive).
 * Falls back to closest Levenshtein match when the saved label differs by a few characters.
 * Logs are for DevTools — filter console by `[ProductSpecificPackaging]`.
 */
function findPackagingMaterialOption(options, raw) {
  if (!Array.isArray(options) || raw == null) return null;
  const candidates = [];
  if (typeof raw === "object" && raw && !raw.isCategory) {
    [raw.code, raw.value, raw.name, raw.title].forEach((x) => {
      if (x != null && String(x).trim() !== "") {
        candidates.push(String(x).trim());
      }
    });
  } else {
    const s = typeof raw === "string" ? raw.trim() : String(raw).trim();
    if (s) candidates.push(s);
  }
  if (!candidates.length) return null;

  for (const c of candidates) {
    const nc = normStr(c);
    if (!nc) continue;
    const hit = options.find(
      (opt) =>
        opt &&
        !opt.isCategory &&
        (normStr(opt.code) === nc ||
          normStr(opt.value) === nc ||
          normStr(opt.name) === nc ||
          normStr(opt.title) === nc)
    );
    if (hit) return hit;
  }

  for (const c of candidates) {
    const nc = normStr(c);
    if (!nc) continue;
    const maxDist = Math.min(10, Math.max(2, Math.floor(nc.length * 0.08)));
    let best = null;
    let bestD = Infinity;
    for (const opt of options) {
      if (!opt || opt.isCategory) continue;
      for (const field of [opt.name, opt.title, opt.value, opt.code]) {
        if (field == null || typeof field !== "string") continue;
        const nf = normStr(field);
        if (!nf) continue;
        const d = levenshtein(nc, nf);
        if (d < bestD) {
          bestD = d;
          best = opt;
        }
      }
    }
    if (best != null && bestD <= maxDist) return best;
  }
  return null;
}

/** Display/save: prefer API `qty_unit`, else dropdown material `unit`, else cached `unitMaterials`. */
function packagingQtyUnitLabel(rowData, rowId, materialDropdown, unitMaterials) {
  const fromApi = rowData?.qty_unit;
  if (fromApi != null && String(fromApi).trim() !== "") return fromApi;
  const materialCode = materialPrimitiveFromRow(rowData);
  if (materialCode) {
    const foundMaterial = findPackagingMaterialOption(
      materialDropdown,
      materialCode
    );
    if (foundMaterial?.unit) return foundMaterial.unit;
  }
  return unitMaterials[rowId] ?? "xx";
}

function materialPrimitiveFromRow(row) {
  if (!row) return null;
  const m = row.material;
  const inp = row.input;
  if (typeof m === "string") return m;
  if (m && typeof m === "object" && !m.isCategory) {
    return m.value ?? m.code ?? m.name ?? m.title ?? null;
  }
  if (m != null) return m;
  if (typeof inp === "string") return inp;
  if (inp && typeof inp === "object") return inp.value ?? inp;
  return inp ?? null;
}

function logPackagingMaterialResolve(stage, payload) {
  console.info("[ProductSpecificPackaging]", stage, payload);
}

/** When exact match fails: substring hints so DevTools shows near-miss names/codes. */
function packagingMatchHints(options, raw) {
  const mats = (options || []).filter((o) => o && !o.isCategory);
  const primitives = [];
  if (raw == null) {
    return { primitives, substringHits: [] };
  }
  if (typeof raw === "object" && !raw.isCategory) {
    [raw.code, raw.value, raw.name, raw.title].forEach((x) => {
      if (x != null && String(x).trim() !== "") primitives.push(String(x).trim());
    });
  } else {
    const s = typeof raw === "string" ? raw.trim() : String(raw).trim();
    if (s) primitives.push(s);
  }
  const needle = normStr(primitives[0] || "");
  if (!needle || !mats.length) {
    return { primitives, substringHits: [] };
  }
  const substringHits = mats
    .filter(
      (o) =>
        normStr(o.name).includes(needle) ||
        needle.includes(normStr(o.name)) ||
        normStr(o.code).includes(needle) ||
        normStr(o.value).includes(needle)
    )
    .slice(0, 6)
    .map((o) => ({
      code: o.code,
      name: o.name,
      value: o.value,
    }));
  return { primitives, substringHits };
}

/** Prefer primitive string for matching; avoids `|| object` leaving a non-plain materialValue. */
function materialValueForSave(row) {
  const v =
    row.material?.value ??
    row.material?.code ??
    row.material?.name ??
    row.material?.title ??
    row.material;
  const i =
    row.input?.value ??
    row.input?.code ??
    row.input?.name ??
    row.input;
  if (v != null && typeof v === "object" && !v.isCategory) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") return v.trim();
  if (i != null && typeof i === "object" && !i.isCategory) {
    return i;
  }
  if (typeof i === "string" && i.trim() !== "") return i.trim();
  if (v != null && v !== "") return v;
  return i ?? null;
}

const ProductSpecificPackagingTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);

  // Fetch material data
  useEffect(() => {
    getProductSpecificPackagingMaterialDropdown({
      setDropdown: setMaterialDropdown,
    });
  }, []);

  useEffect(() => {
    const mats = materialDropdown.filter((r) => r && !r.isCategory);
    if (!mats.length) return;
    const withoutCode = mats.filter(
      (r) => r.code == null || String(r.code).trim() === ""
    );
    logPackagingMaterialResolve("dropdown:loaded", {
      materialOptions: mats.length,
      optionsWithEmptyCode: withoutCode.length,
      sampleNamesMissingCode: withoutCode.slice(0, 8).map((m) => m.name),
    });
  }, [materialDropdown]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllProductSpecificPackaging({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        if (row.qty_unit != null && String(row.qty_unit).trim() !== "") {
          newUnitMaterials[row.id] = row.qty_unit;
          return;
        }
        const materialCode = materialPrimitiveFromRow(row);
        if (materialCode) {
          const selectedMaterial = findPackagingMaterialOption(
            materialDropdown,
            materialCode
          );
          if (selectedMaterial?.unit) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, materialDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      material: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = findPackagingMaterialOption(
            dropdownData || [],
            selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "material",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialValue = materialValueForSave(editedItem);

      const foundMaterial = findPackagingMaterialOption(
        materialDropdown || [],
        materialValue
      );

      const codeFromOption =
        foundMaterial?.code != null &&
        String(foundMaterial.code).trim() !== ""
          ? String(foundMaterial.code).trim()
          : null;

      const materialStringForApi =
        typeof materialValue === "string"
          ? materialValue
          : materialValue && typeof materialValue === "object"
            ? foundMaterial?.code ||
              foundMaterial?.value ||
              materialValue.code ||
              materialValue.value ||
              materialValue.name ||
              materialValue.title ||
              ""
            : materialValue != null
              ? String(materialValue)
              : "";

      const materialCode = codeFromOption || materialStringForApi;
      const existingPc =
        editedItem.product_code != null &&
        String(editedItem.product_code).trim() !== ""
          ? String(editedItem.product_code).trim()
          : null;
      const product_code = codeFromOption ?? existingPc ?? null;

      const hints =
        foundMaterial == null
          ? packagingMatchHints(materialDropdown, materialValue)
          : null;

      logPackagingMaterialResolve("save:edit", {
        rowId: editedItem.id,
        materialValue:
          materialValue && typeof materialValue === "object"
            ? {
                _object: true,
                code: materialValue.code,
                value: materialValue.value,
                name: materialValue.name,
                title: materialValue.title,
              }
            : materialValue,
        matchHintsIfNoMatch: hints,
        matchedOption: foundMaterial
          ? {
              id: foundMaterial.id,
              code: foundMaterial.code,
              value: foundMaterial.value,
              name: foundMaterial.name,
            }
          : null,
        materialCodeSent: materialCode,
        product_codeSent: product_code,
        productCodeNullReason: !foundMaterial
          ? "no_dropdown_row_matched_material_or_input"
          : !codeFromOption
            ? "dropdown_item_has_empty_or_missing_code_inventory_must_supply_code"
            : null,
      });

      const qty_unit =
        foundMaterial?.unit ??
        (editedItem.qty_unit != null && String(editedItem.qty_unit).trim() !== ""
          ? editedItem.qty_unit
          : null);

      updateProductSpecificPackaging({
        productId,
        packagingId: editedItem.id,
        material: materialCode, // Send code to backend
        qty: editedItem.qty,
        qty_unit,
        returnable_packaging: editedItem.returnable_packaging,
        multi_use_packaging: editedItem.multi_use_packaging,
        return_packaging: editedItem.return_packaging,
        supplier: editedItem.supplier,
        input: materialCode, // Keep for backward compatibility
        objects: data,
        setObjects: setData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, data, setData, materialDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialValue = materialValueForSave(newItem);

      const foundMaterial = findPackagingMaterialOption(
        materialDropdown || [],
        materialValue
      );

      const codeFromOption =
        foundMaterial?.code != null &&
        String(foundMaterial.code).trim() !== ""
          ? String(foundMaterial.code).trim()
          : null;

      const materialStringForApi =
        typeof materialValue === "string"
          ? materialValue
          : materialValue && typeof materialValue === "object"
            ? foundMaterial?.code ||
              foundMaterial?.value ||
              materialValue.code ||
              materialValue.value ||
              materialValue.name ||
              materialValue.title ||
              ""
            : materialValue != null
              ? String(materialValue)
              : "";

      const materialCode = codeFromOption || materialStringForApi;
      const product_code = codeFromOption ?? "";

      const hints =
        foundMaterial == null
          ? packagingMatchHints(materialDropdown, materialValue)
          : null;

      logPackagingMaterialResolve("save:add", {
        materialValue:
          materialValue && typeof materialValue === "object"
            ? {
                _object: true,
                code: materialValue.code,
                value: materialValue.value,
                name: materialValue.name,
                title: materialValue.title,
              }
            : materialValue,
        matchHintsIfNoMatch: hints,
        matchedOption: foundMaterial
          ? {
              id: foundMaterial.id,
              code: foundMaterial.code,
              value: foundMaterial.value,
              name: foundMaterial.name,
            }
          : null,
        materialCodeSent: materialCode,
        product_codeSent: product_code || null,
        productCodeNullReason: !foundMaterial
          ? "no_dropdown_row_matched_material_or_input"
          : !codeFromOption
            ? "dropdown_item_has_empty_or_missing_code_inventory_must_supply_code"
            : null,
      });

      addProductSpecificPackaging({
        productId,
        material: materialCode, // Send code to backend
        qty: newItem.qty,
        qty_unit: foundMaterial?.unit ?? null,
        returnable_packaging: newItem.returnable_packaging,
        multi_use_packaging: newItem.multi_use_packaging,
        return_packaging: newItem.return_packaging,
        supplier: newItem.supplier,
        input: materialCode, // Keep for backward compatibility
        callback: fetchData,
        use_swal: true,
        product_code: product_code,
      });
    },
    [productId, fetchData, materialDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductSpecificPackaging({
        productId,
        packagingId: itemToDelete.id,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  // Navigation handlers for impact categories
  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  const columns = useMemo(() => {
    const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
    // Backend returns impact categories directly (e.g., global_warming), not nested
    const impactCategoryAccessor = currentImpactCategory.key;

    return [
      {
        accessor: "material",
        header: `${t("MATERIAL") || "Material"}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: materialDropdown,
        disableSortBy: true,
        unit: (rowId, rowData) =>
          packagingQtyUnitLabel(rowData, rowId, materialDropdown, unitMaterials),
      },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"}*`,
        type: "number",
        tipHeader: "Tip",
        tipBody: t("TIP_QTY_FUNC_COMP"),
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "180px",
        unit: (rowId, rowData) =>
          packagingQtyUnitLabel(rowData, rowId, materialDropdown, unitMaterials),
      },
      {
        accessor: "returnable_packaging",
        header: t("RETURNABLE_PACKAGING") || "Returnable packaging",
        type: "switch",
        choice1: "Yes",
        choice2: "No",
        tipHeader: "Tip",
        tipBody:
          t("RETURNABLE_PACKAGING_TIP") ||
          "Packaging that can be returned by the user or customer to the producer, supplier, or distributor for the purpose of reuse, refilling, or recycling.\n\ne.g. Beverage bottles or transport crates that are sent back to the manufacturer after use",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "multi_use_packaging",
        header: t("MULTI_USE_PACKAGING") || "Multi-use packaging",
        type: "switch",
        choice1: "Yes",
        choice2: "No",
        tipHeader: "Tip",
        tipBody:
          t("MULTI_USE_PACKAGING_TIP") ||
          "Packaging designed and manufactured to withstand multiple cycles of use for the same purpose without losing its functionality, safety, or appearance.\n\ne.g. Durable plastic containers used for repeated deliveries between supplier and customer",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "return_packaging",
        header: t("RETURN_PACKAGING") || "Return packaging",
        type: "switch",
        choice1: "Yes",
        choice2: "No",
        tipHeader: "Tip",
        tipBody:
          t("RETURN_PACKAGING_TIP") ||
          "Packaging intended for the reverse logistics process, i.e., used to return products, components, or materials from the customer back to the supplier, manufacturer, or recycler.\n\ne.g. Protective boxes used to ship back electronic devices for repair or remanufacture",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "supplier",
        header: t("SUPPLIER") || "Supplier",
        type: "text",
        disableSortBy: true,
        placeholder: t("USER_TYPES") || "user types...",
        width: "150px",
      },
      {
        accessor: impactCategoryAccessor,
        header: () => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Tooltip title="Previous impact category">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousImpactCategory();
                  }}
                  style={{ padding: "4px" }}
                >
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
              </Tooltip>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <span>
                    {currentCategory.name}{" "}
                    {currentCategory.unit && `(${currentCategory.unit})`}
                  </span>
                  <TipBox
                    title={currentCategory.name}
                    message={`Impact category: ${currentCategory.name}. Unit: ${
                      currentCategory.unit || "N/A"
                    }`}
                  >
                    <IconButton size="small" style={{ padding: "2px" }}>
                      <InfoIcon />
                    </IconButton>
                  </TipBox>
                </div>
              </div>
              <Tooltip title="Next impact category">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImpactCategory();
                  }}
                  style={{ padding: "4px" }}
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Tooltip>
              <TableCompareTrigger />
            </div>
          );
        },
        type: "custom",
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming, not impact_global_warming)
          const impactValue =
            row.original[currentCategory.key] || // Direct field from API (e.g., global_warming)
            row.original[`impact_${currentCategory.key}`] || // Fallback for nested structure
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        optional: true,
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ];
  }, [
    materialDropdown,
    t,
    unitMaterials,
    currentImpactCategoryIndex,
    handlePreviousImpactCategory,
    handleNextImpactCategory,
  ]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  // Backend returns impact categories directly (e.g., global_warming), not nested
  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material: materialDropdown,
    }),
    [materialDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProductSpecificPackagingTable;

